import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RichTextEditor } from '@/components/RichTextEditor';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Newspaper, Plus, Pencil, Trash2, ArrowLeft, Loader2, ImagePlus, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { title: '', excerpt: '', body: '', featured_image: null, status: 'draft' };

export default function RegionalAdminNews() {
  const [user, setUser] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [u, n] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/regional-admin/news`, { withCredentials: true }),
      ]);
      setUser(u.data); setArticles(n.data || []);
    } catch (e) { toast.error('Failed to load news'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ title: a.title, excerpt: a.excerpt || '', body: a.body || '', featured_image: a.featured_image || null, status: a.status });
    setOpen(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await axios.post(`${BACKEND_URL}/api/regional-admin/news/upload-image`, fd, { withCredentials: true });
      setForm((f) => ({ ...f, featured_image: data.url }));
      toast.success('Image uploaded');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const save = async (status) => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    const payload = { ...form, status: status || form.status };
    try {
      if (editing) await axios.put(`${BACKEND_URL}/api/regional-admin/news/${editing.id}`, payload, { withCredentials: true });
      else await axios.post(`${BACKEND_URL}/api/regional-admin/news`, payload, { withCredentials: true });
      toast.success(payload.status === 'published' ? 'Article published' : 'Draft saved');
      setOpen(false); await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    try { await axios.delete(`${BACKEND_URL}/api/regional-admin/news/${a.id}`, { withCredentials: true }); toast.success('Deleted'); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <Layout user={user}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate('/regional-admin')} data-testid="ra-news-back"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand mb-4 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Regional Admin
        </button>
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-lg bg-brand-light flex items-center justify-center"><Newspaper className="w-5 h-5 text-brand" /></span>
            <div><h1 className="text-2xl font-bold text-slate-900 leading-tight">Manage News</h1>
              <p className="text-sm text-slate-500">Publish updates for employers in your region</p></div>
          </div>
          <Button onClick={openNew} data-testid="ra-news-new" className="bg-brand hover:bg-brand-hover text-white"><Plus className="w-4 h-4 mr-1.5" /> New Article</Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand" /></div>
        ) : articles.length === 0 ? (
          <Card className="p-12 text-center border border-slate-200"><p className="text-slate-700 font-medium">No articles yet</p>
            <p className="text-sm text-slate-400 mt-1">Create your first article for your region.</p></Card>
        ) : (
          <div className="space-y-3" data-testid="ra-news-list">
            {articles.map((a) => (
              <div key={a.id} data-testid={`ra-news-row-${a.id}`} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
                {a.featured_image
                  ? <img src={a.featured_image} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                  : <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0"><Newspaper className="w-6 h-6 text-slate-300" /></div>}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-900 truncate">{a.title}</h3>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${a.status === 'published' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>{a.status}</span>
                  </div>
                  {a.excerpt && <p className="text-sm text-slate-500 truncate mt-0.5">{a.excerpt}</p>}
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <Button onClick={() => openEdit(a)} data-testid={`ra-news-edit-${a.id}`} variant="outline" size="sm" className="border-slate-200"><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button onClick={() => remove(a)} data-testid={`ra-news-delete-${a.id}`} variant="outline" size="sm" className="border-slate-200 text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ra-news-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit Article' : 'New Article'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div><Label className="text-sm">Title</Label>
              <Input data-testid="ra-news-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" placeholder="Article title" /></div>
            <div><Label className="text-sm">Short excerpt</Label>
              <Textarea data-testid="ra-news-excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="mt-1 min-h-[60px]" placeholder="One or two sentences shown on cards" /></div>
            <div><Label className="text-sm">Body</Label>
              <div className="mt-1">
                <RichTextEditor full testId="ra-news-body" value={form.body}
                  onChange={(html) => setForm({ ...form, body: html })}
                  placeholder="Full article text. Use the toolbar for headings, lists, and links." />
              </div></div>
            <div>
              <Label className="text-sm">Featured image</Label>
              <div className="mt-1 flex items-center gap-3">
                {form.featured_image && (
                  <div className="relative">
                    <img src={form.featured_image} alt="" className="w-24 h-16 rounded-lg object-cover border border-slate-200" />
                    <button type="button" onClick={() => setForm({ ...form, featured_image: null })} aria-label="Remove image"
                      className="absolute -top-2 -right-2 bg-white rounded-full border border-slate-200 p-0.5 text-slate-500 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
                  </div>
                )}
                <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="ra-news-image-input" />
                <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()} className="border-slate-200" data-testid="ra-news-image-btn">
                  {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ImagePlus className="w-4 h-4 mr-1.5" />}
                  {form.featured_image ? 'Replace' : 'Upload image'}
                </Button>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={() => save('draft')} disabled={saving} className="border-slate-300" data-testid="ra-news-save-draft">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Save draft
            </Button>
            <Button onClick={() => save('published')} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid="ra-news-publish">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Publish
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
